import { useEffect, useState, type FormEvent } from 'react';
import { Bell, Save, Loader2, AlertCircle, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNotifications, useSaveTelegram, useTestTelegram } from '@/hooks/useNotifications';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export function PlatformNotificationsPage() {
  const { data, isPending } = useNotifications();
  const save = useSaveTelegram();
  const test = useTestTelegram();

  const [enabled, setEnabled] = useState(false);
  const [onInbound, setOnInbound] = useState(true);
  const [onPurchase, setOnPurchase] = useState(true);
  const [onError, setOnError] = useState(true);
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.telegram) return;
    setEnabled(data.telegram.enabled);
    setOnInbound(data.telegram.on_inbound_email);
    setOnPurchase(data.telegram.on_purchase);
    setOnError(data.telegram.on_error);
    setChatId(data.telegram.chat_id ?? '');
  }, [data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        enabled,
        on_inbound_email: onInbound,
        on_purchase: onPurchase,
        on_error: onError,
        bot_token: botToken || undefined,
        chat_id: chatId || undefined,
      });
      setBotToken('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const onTest = async () => {
    setTestResult(null);
    try {
      await test.mutateAsync();
      setTestResult('✓ Test mesajı gönderildi. Telegram\'ı kontrol et.');
    } catch (err) {
      setTestResult(`✗ ${err instanceof ApiError ? err.message : 'Gönderilemedi'}`);
    }
  };

  if (isPending || !data) return <div className="text-muted-foreground">Yükleniyor…</div>;
  const s = data.telegram;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5" /> Bildirimler
        </h1>
        <p className="text-muted-foreground text-sm">
          Platform olayları için harici bildirim kanalları. Şu an Telegram destekleniyor.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Mevcut Durum
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          <Row label="Durum" value={s.enabled ? 'Açık' : 'Kapalı'} ok={s.enabled} />
          <Row label="Bot Token" value={s.bot_token_hint ?? '—'} ok={s.bot_token_set} />
          <Row label="Chat ID" value={s.chat_id ?? '—'} ok={!!s.chat_id} />
          <Row label="Gelen e-postada bildir" value={s.on_inbound_email ? 'Evet' : 'Hayır'} ok={s.on_inbound_email} />
          <Row label="Satın almada bildir" value={s.on_purchase ? 'Evet' : 'Hayır'} ok={s.on_purchase} />
          <Row label="Hatada bildir" value={s.on_error ? 'Evet' : 'Hayır'} ok={s.on_error} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nasıl Kurulur?</CardTitle>
          <CardDescription>
            Telegram bot oluştur, kendi Telegram hesabınla bot'a bir mesaj at, aşağıdaki alanlara bilgileri gir.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
            <li>
              Telegram'da{' '}
              <a
                href="https://t.me/BotFather"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                @BotFather
              </a>
              'a <code className="bg-muted px-1 py-0.5 rounded text-xs">/newbot</code> yaz → isim seç → sana bir token verir.
            </li>
            <li>
              Oluşturduğun bot'a <strong>kendi hesabından</strong> bir mesaj at (ör: "selam") — aksi halde bot sana yazamaz.
            </li>
            <li>
              Tarayıcıda aç:{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded break-all">
                https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates
              </code>
              {' '}— cevapta <code className="text-xs">chat.id</code> değerini kopyala.
            </li>
            <li>Token ve chat ID'yi aşağıya yapıştır, "Aktif" kutusunu işaretle, kaydet.</li>
          </ol>
        </CardContent>
      </Card>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Telegram Yapılandırması</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2.5">
              <input
                id="tg-enabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor="tg-enabled" className="cursor-pointer text-sm font-medium">
                  Telegram bildirimleri aktif
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Kapalıyken bildirim gönderilmez, ama token/chat id korunur.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bt">
                Bot Token{' '}
                {s.bot_token_hint && (
                  <span className="text-xs text-muted-foreground">(yenilemek için yeni token yaz)</span>
                )}
              </Label>
              <Input
                id="bt"
                type="password"
                autoComplete="off"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder={s.bot_token_hint ? '••••••••' : '123456789:AAH…'}
              />
              <p className="text-xs text-muted-foreground">
                @BotFather'dan aldığın token. AES-GCM ile şifrelenerek saklanır.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cid">Chat ID</Label>
              <Input
                id="cid"
                type="text"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="örn: 123456789 veya -1001234567890"
              />
              <p className="text-xs text-muted-foreground">
                Sayısal ID. Kişisel chat için pozitif, grup için negatif başlar.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Bildirim olayları</Label>

              <div className="flex items-start gap-2 rounded-md border px-3 py-2.5">
                <input
                  id="tg-inbound"
                  type="checkbox"
                  checked={onInbound}
                  onChange={(e) => setOnInbound(e.target.checked)}
                  className="h-4 w-4 rounded border-input mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="tg-inbound" className="cursor-pointer text-sm font-medium">
                    📧 Gelen e-posta
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Resend inbound webhook'a bir e-posta düştüğünde özet gönderir.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border px-3 py-2.5">
                <input
                  id="tg-purchase"
                  type="checkbox"
                  checked={onPurchase}
                  onChange={(e) => setOnPurchase(e.target.checked)}
                  className="h-4 w-4 rounded border-input mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="tg-purchase" className="cursor-pointer text-sm font-medium">
                    💰 Yeni satın alma
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Kripto (auto/manual) veya Stripe ile paket/eklenti satın alındığında tetiklenir.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border px-3 py-2.5">
                <input
                  id="tg-error"
                  type="checkbox"
                  checked={onError}
                  onChange={(e) => setOnError(e.target.checked)}
                  className="h-4 w-4 rounded border-input mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="tg-error" className="cursor-pointer text-sm font-medium">
                    🚨 Kullanıcı hatası (HTTP 500)
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Beklenmedik backend hatası oluştuğunda (kullanıcı arayüzde hata görür) Telegram'a özet + stack trace gönderir.
                    Aynı hata için 5 dakikada tek uyarı.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
        {saved && (
          <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
            Kaydedildi ✓
          </div>
        )}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Kaydet
        </Button>
      </form>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4" /> Test Bildirimi
          </CardTitle>
          <CardDescription>Ayarların çalıştığını doğrulamak için kendi bot'una bir test mesajı yolla.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={onTest}
            disabled={test.isPending || !s.enabled || !s.bot_token_set || !s.chat_id}
          >
            {test.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Test Mesajı Gönder
          </Button>
          {testResult && (
            <div
              className={cn(
                'mt-3 text-sm rounded-md px-3 py-2',
                testResult.startsWith('✓')
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-destructive/10 text-destructive',
              )}
            >
              {testResult}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b last:border-b-0 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-mono', ok ? 'text-foreground' : 'text-muted-foreground')}>{value}</span>
    </div>
  );
}
